/**
 * Загрузки и сверка.
 *
 * Здесь видно, можно ли доверять цифрам в отчётах: когда данные приехали,
 * что сломалось и почему витрины не обновились.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { api, ApiError } from '../api'
import type { BranchConflict, DashboardData } from '../types'
import { Badge, Card, Empty, Loading, Notice, TableWrap } from '../components/ui'
import type { BatchReport } from '../types'
import { formatDate, formatDateTime, formatMoney, formatNumber, formatPercent, formatRelative, pluralize } from '../format'
import { STREAM_TITLES } from './Dashboard'

const streamTitle = (code: string) => STREAM_TITLES[code] ?? code

/** «transform:sales» — служебное имя задания; человеку — что оно делает. */
function jobTitle(job: string, stream: string | null): string {
  const [kind, code] = job.split(':')
  const subject = streamTitle(stream ?? code ?? '')
  if (kind === 'transform') return `Разбор пакета: ${subject.toLowerCase()}`
  if (kind === 'rebuild') return `Пересчёт из сохранённых пакетов${code ? `: ${subject.toLowerCase()}` : ''}`
  if (kind === 'marts' || kind === 'refresh') return 'Обновление витрин отчётов'
  if (kind === 'quality') return 'Проверка качества данных'
  return job
}

/** Проверки качества: что значит строка и где порог. */
const QUALITY: Record<string, { title: string; limit: string; info?: boolean }> = {
  'sales.department_undefined': { title: 'Продажи без подразделения', limit: 'норма — до 5% строк' },
  'sales.branch_undefined': { title: 'Филиал не определился', limit: 'норма — до 5% строк' },
  'sales.branch_conflict': {
    title: 'Склад и подразделение в разных городах',
    limit:
      'Ошибка ввода в 1С: например, склад Челябинска, а подразделение екатеринбургское. Выручка таких строк отнесена ' +
      'к городу подразделения. Данные мы не меняем — документы поправляются в 1С, список ниже. Норма — ни одной строки',
  },
  'sales.cost_missing': { title: 'Строки без себестоимости', limit: 'для сведения: услуги идут без себестоимости', info: true },
}

const SHOWN = 10

/** Показать первые N строк и кнопку «ещё». */
function useMore<T>(items: T[]): [T[], ReactNode] {
  const [all, setAll] = useState(false)
  const shown = all ? items : items.slice(0, SHOWN)
  const button =
    items.length > SHOWN ? (
      <div className="panel__more">
        <button type="button" className="link-button" onClick={() => setAll((value) => !value)} aria-expanded={all}>
          {all ? 'Свернуть' : `Показать ещё ${items.length - SHOWN}`}
        </button>
      </div>
    ) : null
  return [shown, button]
}

interface EtlRun {
  id: string
  job_name: string
  stream: string | null
  status: string
  started_at: string
  finished_at: string | null
  rows_in: number | null
  rows_upserted: number | null
  rows_deleted: number | null
  error: string | null
  is_stale: boolean
}

interface Check {
  id: string
  batch_id?: string | null
  stream: string
  metric: string
  period_from: string | null
  period_to: string | null
  source_value: string | null
  our_value: string | null
  diff: string | null
  is_ok: boolean
  checked_at: string
}

const METRIC_TITLES: Record<string, string> = {
  amount_net: 'Сумма без НДС',
  amount_gross: 'Выручка с НДС',
  // Контрольная сумма пакета — по cost_amount, а это стоимость закупки
  // без доп. расходов. Полная себестоимость в отчётах — с ними.
  cost_amount: 'Стоимость закупки',
  amount: 'Сумма',
  quantity: 'Количество',
  rows_count: 'Число строк',
  debit_turnover: 'Оборот по дебету',
  credit_turnover: 'Оборот по кредиту',
}

export function DataHealth() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [runs, setRuns] = useState<EtlRun[]>([])
  const [checks, setChecks] = useState<Check[]>([])
  const [batches, setBatches] = useState<BatchReport[]>([])
  const [conflicts, setConflicts] = useState<BranchConflict[]>([])
  const [error, setError] = useState<string | null>(null)
  const [partial, setPartial] = useState<string[]>([])

  useEffect(() => {
    /**
     * Каждый блок грузится сам по себе.
     *
     * Раньше это был Promise.all, и отказ любого из четырёх запросов
     * оставлял вместо всей страницы одну строку с ошибкой. Страница
     * состояния данных — последнее место, которое имеет право пропадать
     * целиком: на неё приходят как раз тогда, когда что-то сломалось.
     */
    const load = async () => {
      const missing: string[] = []

      const [summary, runsResponse, checksResponse, batchesResponse, conflictsResponse] = await Promise.allSettled([
        api.dashboard(),
        fetch('/api/etl/runs', { credentials: 'include' }).then((response) => response.json()),
        fetch('/api/etl/reconciliation', { credentials: 'include' }).then((response) => response.json()),
        api.etlBatches(20),
        api.branchConflicts(),
      ])

      if (summary.status === 'fulfilled') setDashboard(summary.value)
      else setError(summary.reason instanceof ApiError ? summary.reason.message : 'Не удалось загрузить сводку')

      if (runsResponse.status === 'fulfilled') setRuns(runsResponse.value.runs ?? [])
      else missing.push('журнал загрузок')

      if (checksResponse.status === 'fulfilled') setChecks(checksResponse.value.checks ?? [])
      else missing.push('сверка итогов')

      if (batchesResponse.status === 'fulfilled') setBatches(batchesResponse.value.batches ?? [])
      else missing.push('замечания к пакетам')

      // Расшифровка — дополнение к проверке, а не отдельный блок: без неё
      // остаётся число в таблице качества, и о сбое молчим.
      if (conflictsResponse.status === 'fulfilled') setConflicts(conflictsResponse.value.conflicts ?? [])

      setPartial(missing)
    }

    void load()
  }, [])

  if (error && !dashboard) return <Notice tone="bad">{error}</Notice>
  if (!dashboard)
    return (
      // Та же композиция, что у готовой страницы: две карточки рядом,
      // «Замечания к пакетам», «Сверка итогов» — заголовки на своих местах.
      <div className="stack" style={{ gap: 18 }} aria-busy="true">
        <div className="grid grid--2">
          <Card title="Актуальность по источникам" subtitle="На какое время данные в 1С и когда они к нам приехали" flush>
            <Loading rows={5} columns={3} />
          </Card>
          <Card title="Сохранённые пакеты от 1С" subtitle="Пакеты хранятся целиком, как пришли. Любой показатель пересчитывается из них без повторной выгрузки из 1С" flush>
            <Loading rows={5} columns={3} />
          </Card>
        </div>
        <Card title="Замечания к пакетам" subtitle="Пакет принят и загружен, но данные приедут неполными. Это чинится на стороне 1С" flush>
          <Loading rows={2} columns={3} />
        </Card>
        <Card title="Сверка итогов" subtitle="Суммы, которые заявила 1С, против посчитанных у нас — по каждому пакету. При расхождении факты не записываются" flush>
          <Loading rows={6} />
        </Card>
      </div>
    )

  const failedChecks = checks.filter((check) => !check.is_ok && check.stream !== 'quality')
  const withWarnings = batches.filter((batch) => batch.warnings.length > 0)

  // Сверка: строки одного пакета — одна запись. Сошлось — нейтральный текст,
  // цветом выделяются только расхождения.
  const groups = new Map<string, Check[]>()
  for (const check of checks.filter((item) => item.stream !== 'quality')) {
    const key = check.batch_id ?? `${check.stream}@${check.checked_at.slice(0, 19)}`
    groups.set(key, [...(groups.get(key) ?? []), check])
  }
  const reconciliations = [...groups.entries()].map(([key, items]) => ({ key, items, ok: items.every((item) => item.is_ok), at: items[0]!.checked_at, stream: items[0]!.stream }))
  // Проверки качества — последний снимок по каждой метрике.
  const quality = [...new Map(checks.filter((item) => item.stream === 'quality').map((item) => [item.metric, item] as const)).values()]

  return (
    <div className="stack" style={{ gap: 18 }}>
      {partial.length > 0 && (
        <Notice tone="warn">
          Не удалось загрузить: {partial.join(', ')}. Остальное на странице актуально.
        </Notice>
      )}

      {failedChecks.length > 0 && (
        <Notice tone="bad">
          <strong>Сверка не сошлась.</strong> Витрины по этим пакетам не обновлялись — в отчётах остались
          прежние цифры. Показывать вчерашние данные безопаснее, чем сегодняшние неправильные.
        </Notice>
      )}

      <div className="grid grid--2">
        <Card index={0} title="Актуальность по источникам" subtitle="На какое время данные в 1С и когда они к нам приехали" flush>
          {dashboard.freshness.length === 0 ? (
            <Empty icon="data" title="Данные ещё не загружались" hint="Как только 1С пришлёт первый пакет, здесь появится время выгрузки." />
          ) : (
            <TableWrap cards>
              <table className="data">
                <thead>
                  <tr>
                    <th>Источник</th>
                    <th>Данные на</th>
                    <th>Обновлено</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.freshness.map((source) => (
                    <tr key={source.stream}>
                      <td title={source.stream}>{streamTitle(source.stream)}</td>
                      <td>
                        {/* Последний документ — второй строкой: в половине ширины четыре колонки не помещались. */}
                        <div className="mono">{formatDateTime(source.data_as_of)}</div>
                        {source.last_document_date && <div className="tiny muted">последний документ {formatDate(source.last_document_date)}</div>}
                      </td>
                      <td className="secondary">{formatRelative(source.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          )}
        </Card>

        <Card
          index={1}
          title="Сохранённые пакеты от 1С"
          subtitle="Пакеты хранятся целиком, как пришли. Любой показатель пересчитывается из них без повторной выгрузки из 1С"
          flush
        >
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Поток</th>
                  <th className="num">Пакетов</th>
                  <th className="num">Строк</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.staging.map((row) => (
                  <tr key={row.stream}>
                    <td title={row.stream}>{streamTitle(row.stream)}</td>
                    <td className="num">{formatNumber(row.batches)}</td>
                    <td className="num">{formatNumber(row.rows)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Card>
      </div>

      <Card index={2} title="Замечания к пакетам" subtitle="Пакет принят и загружен, но данные приедут неполными. Это чинится на стороне 1С" flush>
        {withWarnings.length === 0 ? (
          <Empty icon="check" title="Замечаний нет" hint="Последние пакеты разобрались без оговорок" />
        ) : (
          <WarningsTable batches={withWarnings} />
        )}
      </Card>

      <Card
        index={3}
        title="Сверка итогов"
        subtitle="Суммы, которые заявила 1С, против посчитанных у нас — по каждому пакету. При расхождении факты не записываются"
        flush
      >
        {reconciliations.length === 0 ? (
          <Empty icon="check" title="Сверок ещё не было" hint="Сверка выполняется при разборе каждого пакета от 1С." />
        ) : (
          <ReconciliationTable groups={reconciliations} />
        )}
      </Card>

      {quality.length > 0 && (
        <Card
          index={4}
          title="Качество данных продаж"
          subtitle={`Доля строк с проблемой среди всех строк продаж · проверено ${formatDateTime(quality[0]!.checked_at)}`}
          flush
        >
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Проверка</th>
                  <th className="num">Строк с проблемой</th>
                  <th className="num">Доля</th>
                  <th>Итог</th>
                </tr>
              </thead>
              <tbody>
                {quality.map((check) => {
                  const meta = QUALITY[check.metric]
                  return (
                    <tr key={check.metric}>
                      <td className="wrap-cell wrap-cell--wide">
                        <div>{meta?.title ?? check.metric}</div>
                        {meta && <div className="tiny muted">{meta.limit}</div>}
                      </td>
                      <td className="num">
                        {/* Число в своём элементе: иначе карточка на телефоне считала ячейку пустой (одна «muted») и прятала её. */}
                        <span>{formatNumber(check.our_value)}</span> <span className="muted">из {formatNumber(check.source_value)}</span>
                      </td>
                      <td className="num">{formatPercent(check.diff)}</td>
                      <td>
                        {check.is_ok ? (
                          <span className="secondary">{meta?.info ? 'для сведения' : 'в норме'}</span>
                        ) : (
                          <Badge tone="warn">выше нормы</Badge>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </TableWrap>
          {conflicts.length > 0 && <ConflictsTable conflicts={conflicts} />}
        </Card>
      )}

      <Card index={5} title="Журнал загрузок" subtitle={pluralize(runs.length, 'последний прогон', 'последних прогона', 'последних прогонов')} flush>
        {runs.length === 0 ? <Empty icon="log" title="Прогонов ещё не было" /> : <RunsTable runs={runs} />}
      </Card>
    </div>
  )
}

/**
 * Где искать строки с конфликтом филиала: пара «склад — подразделение»
 * и номера документов. Число строк в 1С не найти, номер документа — за минуту.
 */
function ConflictsTable({ conflicts }: { conflicts: BranchConflict[] }) {
  const [shown, more] = useMore(conflicts)
  return (
    <>
      <div className="tiny muted" style={{ padding: '14px 18px 4px' }}>
        Склад и подразделение в разных городах — где искать в 1С. Город продажи в отчётах — по подразделению.
      </div>
      <TableWrap cards>
        <table className="data">
          <thead>
            <tr>
              <th>Склад</th>
              <th>Подразделение</th>
              <th className="num">Документов</th>
              <th>Документы</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={`${row.warehouse}|${row.department}`}>
                <td>
                  <div>{row.warehouse}</div>
                  <div className="tiny muted">{row.warehouse_branch}</div>
                </td>
                <td>
                  <div>{row.department}</div>
                  <div className="tiny muted">{row.department_branch} — сюда отнесена выручка</div>
                </td>
                <td className="num">
                  <span>{formatNumber(row.documents)}</span> <span className="muted">· {pluralize(row.lines, 'строка', 'строки', 'строк')}</span>
                </td>
                <td style={{ whiteSpace: 'normal', minWidth: 220 }}>
                  <div className="tiny">{row.numbers.join(', ')}{row.documents > row.numbers.length ? ' и другие' : ''}</div>
                  <div className="tiny muted">
                    {row.first_date === row.last_date ? formatDate(row.first_date) : `${formatDate(row.first_date)} — ${formatDate(row.last_date)}`}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      {more}
    </>
  )
}

function WarningsTable({ batches }: { batches: BatchReport[] }) {
  const rows = batches.flatMap((batch) => batch.warnings.map((warning) => ({ batch, warning })))
  const [shown, more] = useMore(rows)
  return (
    <>
      <TableWrap cards>
        <table className="data">
          <thead>
            <tr>
              <th>Пакет</th>
              <th>Замечание</th>
              <th className="num">Строк</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(({ batch, warning }) => (
              <tr key={`${batch.batch_uid}:${warning.code}`}>
                <td>
                  <div>{streamTitle(batch.stream)}</div>
                  <div className="tiny muted">
                    {batch.period ? `${formatDate(batch.period.from)} — ${formatDate(batch.period.to)}` : batch.mode === 'full' ? 'целиком' : 'снимок'}
                    {' · '}
                    <code>{batch.batch_uid.slice(0, 8)}</code>
                  </div>
                </td>
                <td style={{ whiteSpace: 'normal', minWidth: 260 }}>{warning.message}</td>
                <td className="num">{formatNumber(warning.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      {more}
    </>
  )
}

function ReconciliationTable({ groups }: { groups: Array<{ key: string; items: Check[]; ok: boolean; at: string; stream: string }> }) {
  const [shown, more] = useMore(groups)
  return (
    <>
      <TableWrap cards>
        <table className="data">
          <thead>
            <tr>
              <th>Когда</th>
              <th>Поток</th>
              <th>Показатели</th>
              <th>Итог</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((group) => (
              <tr key={group.key} className={group.ok ? undefined : 'row--bad'}>
                <td className="secondary">{formatDateTime(group.at)}</td>
                <td>{streamTitle(group.stream)}</td>
                <td style={{ whiteSpace: 'normal' }}>
                  {group.ok ? (
                    <span className="secondary">{group.items.map((item) => METRIC_TITLES[item.metric] ?? item.metric).join(', ')}</span>
                  ) : (
                    <div className="stack" style={{ gap: 2 }}>
                      {group.items.map((item) => (
                        <div key={item.id} className={item.is_ok ? 'secondary' : undefined}>
                          {METRIC_TITLES[item.metric] ?? item.metric}:{' '}
                          {item.is_ok ? (
                            'сошлось'
                          ) : (
                            <>
                              1С — <span className="mono">{formatMoney(item.source_value, true)}</span>, у нас —{' '}
                              <span className="mono">{formatMoney(item.our_value, true)}</span>,{' '}
                              <span className="cell-negative mono">разница {formatMoney(item.diff, true)}</span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td>{group.ok ? <span className="secondary">сошлось</span> : <Badge tone="bad">расхождение</Badge>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      {more}
    </>
  )
}

function RunsTable({ runs }: { runs: EtlRun[] }) {
  const [shown, more] = useMore(runs)
  return (
    <>
      <TableWrap cards>
        <table className="data">
          <thead>
            <tr>
              <th>Задание</th>
              <th>Начало</th>
              <th className="num">Принято</th>
              <th className="num">Записано</th>
              <th className="num">Погашено</th>
              <th>Итог</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((run) => {
              const bad = run.is_stale || run.status === 'failed'
              return (
                <tr key={run.id} className={bad ? 'row--bad' : undefined}>
                  <td title={run.job_name}>
                    <div>{jobTitle(run.job_name, run.stream)}</div>
                    {run.error && <div className="tiny cell-negative" style={{ whiteSpace: 'normal', maxWidth: 420 }}>{run.error}</div>}
                  </td>
                  <td className="secondary">{formatDateTime(run.started_at)}</td>
                  <td className="num minor">{run.rows_in === null ? '—' : formatNumber(run.rows_in)}</td>
                  <td className="num">{run.rows_upserted === null ? '—' : formatNumber(run.rows_upserted)}</td>
                  <td className="num minor">{run.rows_deleted === null ? '—' : formatNumber(run.rows_deleted)}</td>
                  <td>
                    {run.is_stale ? (
                      <Badge tone="bad">завис</Badge>
                    ) : run.status === 'failed' ? (
                      <Badge tone="bad">ошибка</Badge>
                    ) : run.status === 'success' ? (
                      <span className="secondary">успешно</span>
                    ) : (
                      <Badge tone="warn">идёт</Badge>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </TableWrap>
      {more}
    </>
  )
}
